import React, { useLayoutEffect, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import CreditBadge from '../../credit/CreditBadge';
import { useRegisterInlineHeaderActions } from './HeaderActionsContext';

/** 共通バーが `hidden lg:flex` で切り替わる幅（Tailwind の lg） */
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

/**
 * 要素が実際に表示されているか。`display: none`（`hidden lg:flex` のモバイル幅）だと
 * offsetParent が null になる。jsdom はレイアウトを持たず常に null を返すため、
 * テストでは `HTMLElement.prototype.offsetParent` を差し替えて検証する。
 */
export function isElementVisible(element: HTMLElement | null): boolean {
  return element !== null && element.offsetParent !== null;
}

export interface TaskyHelpLinkProps {
  /** アイコンだけにする（モバイルのトップバーで題名とページの操作の幅を残す） */
  compact?: boolean;
}

export const TaskyHelpLink: React.FC<TaskyHelpLinkProps> = ({ compact = false }) => (
  <a
    href="https://lp.tasky-ai.com/help"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="ヘルプ（新しいタブで開きます）"
    data-track="header-help"
    className={cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-warm-border bg-white text-[13px] font-medium text-gray-700 transition-colors hover:bg-warm-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
      compact ? 'p-1.5' : 'px-3 py-1.5'
    )}
  >
    <HelpCircle size={16} aria-hidden="true" />
    {!compact && <span>ヘルプ</span>}
  </a>
);

export interface TaskyHeaderActionsProps {
  /** App の固定オーバーレイ用。true のときは自己登録しない */
  overlay?: boolean;
  /** ヘルプをアイコンだけにする */
  compact?: boolean;
}

/**
 * ヘルプ・クレジット残高のピル。
 *
 * ページ内（共通バーやモバイルのトップバー）に置かれたときは、自分が実際に表示されて
 * いる間だけ HeaderActionsProvider に登録する。App はこの登録が無いときにだけ固定
 * オーバーレイでピルを出すので、ページ側にピルがあれば被らず、無ければ従来どおり出る。
 */
export const TaskyHeaderActions: React.FC<TaskyHeaderActionsProps> = ({ overlay = false, compact = false }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // 固定オーバーレイのピルが一瞬見えてから消えるのを避けるため、描画前に判定する。
  useLayoutEffect(() => {
    if (overlay) return undefined;

    const update = () => setVisible(isElementVisible(rootRef.current));
    update();

    const mediaQuery =
      typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_MEDIA_QUERY) : null;
    const supportsChangeEvent =
      mediaQuery !== null && typeof mediaQuery.addEventListener === 'function';

    window.addEventListener('resize', update);
    if (supportsChangeEvent) mediaQuery.addEventListener('change', update);

    return () => {
      window.removeEventListener('resize', update);
      if (supportsChangeEvent) mediaQuery.removeEventListener('change', update);
    };
  }, [overlay]);

  useRegisterInlineHeaderActions(!overlay && visible);

  return (
    <div ref={rootRef} className="flex shrink-0 items-center gap-2">
      <TaskyHelpLink compact={compact} />
      <CreditBadge />
    </div>
  );
};
